import { HttpErrorResponse } from '@angular/common/http'
import { Inject, Injectable, inject } from '@angular/core'
import { GbErrorMessageService } from '@galabid/shared/utils/helpers'
import { APP_CONFIG } from 'libs/shared/app-config/src/lib/gb-app-config'
import { EnvironmentModel } from 'apps/gb-dashboard/src/environments/environment.model'
import { Actions, createEffect, ofType } from '@ngrx/effects'
import { Update } from '@ngrx/entity'
import { Action, Store } from '@ngrx/store'
import { SelectedAuctionFacade } from 'libs/gb-dashboard/auctions/data-access-selected-auction/src/lib/+state/selected-auction.facade'
import { Observable, catchError, filter, from, map, mergeMap, of, switchMap, withLatestFrom } from 'rxjs'
import { PresenterContextService } from 'libs/gb-dashboard/presenter/utils/src/lib/gb-presenter-context/presenter-context.service'
import { ProjectorsFacade } from '@galabid/gb-dashboard/projector/data-access'
import { PresenterWidgetsApiService } from './presenter-widgets-api.service'
import { PresenterWidgetsFacade } from './presenter-widgets.facade'
import * as PresenterWidgetsActions from './presenter-widgets.actions'
import { QrCodeWidget, WidgetConfigMap, WidgetModel, WidgetModelMap, WidgetType } from './presenter-widgets.models'
import * as PresenterWidgetsSelectors from './presenter-widgets.selectors'
import { PresenterWidgetsState } from './presenter-widgets.reducer'

type WidgetOperation = 'create' | 'update' | 'delete'

const union = <T extends ReadonlyArray<unknown>>(actions: T): T => actions

const ALL_WIDGET_START_ACTIONS = union([
  PresenterWidgetsActions.createCountdownWidget,
  PresenterWidgetsActions.updateSelectedCountdownWidget,
  PresenterWidgetsActions.deleteCountdownWidget,
  PresenterWidgetsActions.createImageWidget,
  PresenterWidgetsActions.updateSelectedImageWidget,
  PresenterWidgetsActions.deleteImageWidget,
  PresenterWidgetsActions.createTextWidget,
  PresenterWidgetsActions.updateSelectedTextWidget,
  PresenterWidgetsActions.deleteTextWidget,
  PresenterWidgetsActions.createSmartTextWidget,
  PresenterWidgetsActions.updateSelectedSmartTextWidget,
  PresenterWidgetsActions.deleteSmartTextWidget,
  PresenterWidgetsActions.createTotalRaisedWidget,
  PresenterWidgetsActions.updateSelectedTotalRaisedWidget,
  PresenterWidgetsActions.deleteTotalRaisedWidget,
  PresenterWidgetsActions.createTeamScoringWidget,
  PresenterWidgetsActions.updateSelectedTeamScoringWidget,
  PresenterWidgetsActions.deleteTeamScoringWidget,
  PresenterWidgetsActions.createPowerPledgeWidget,
  PresenterWidgetsActions.updateSelectedPowerPledgeWidget,
  PresenterWidgetsActions.deletePowerPledgeWidget,
  PresenterWidgetsActions.createNotificationWidget,
  PresenterWidgetsActions.updateSelectedNotificationWidget,
  PresenterWidgetsActions.deleteNotificationWidget,
  PresenterWidgetsActions.createItemTableWidget,
  PresenterWidgetsActions.updateSelectedItemTableWidget,
  PresenterWidgetsActions.deleteItemTableWidget,
  PresenterWidgetsActions.createRaffleWinnersWidget,
  PresenterWidgetsActions.updateSelectedRaffleWinnersWidget,
  PresenterWidgetsActions.deleteRaffleWinnersWidget,
  PresenterWidgetsActions.createHonourRollWidget,
  PresenterWidgetsActions.updateSelectedHonourRollWidget,
  PresenterWidgetsActions.deleteHonourRollWidget,
  PresenterWidgetsActions.createThermometerWidget,
  PresenterWidgetsActions.updateSelectedThermometerWidget,
  PresenterWidgetsActions.deleteThermometerWidget,
  PresenterWidgetsActions.createRaffleDrawWidget,
  PresenterWidgetsActions.updateSelectedRaffleDrawWidget,
  PresenterWidgetsActions.deleteRaffleDrawWidget,
  PresenterWidgetsActions.createQrCodeWidget,
  PresenterWidgetsActions.updateSelectedQrCodeWidget,
  PresenterWidgetsActions.deleteQrCodeWidget,
  PresenterWidgetsActions.createVideoWidget,
  PresenterWidgetsActions.updateSelectedVideoWidget,
  PresenterWidgetsActions.deleteVideoWidget,
] as const)

const OPERATION_BY_ACTION_TYPE: Record<string, WidgetOperation> = {
  [PresenterWidgetsActions.createCountdownWidget.type]: 'create',
  [PresenterWidgetsActions.updateSelectedCountdownWidget.type]: 'update',
  [PresenterWidgetsActions.deleteCountdownWidget.type]: 'delete',
  [PresenterWidgetsActions.createImageWidget.type]: 'create',
  [PresenterWidgetsActions.updateSelectedImageWidget.type]: 'update',
  [PresenterWidgetsActions.deleteImageWidget.type]: 'delete',
  [PresenterWidgetsActions.createTextWidget.type]: 'create',
  [PresenterWidgetsActions.updateSelectedTextWidget.type]: 'update',
  [PresenterWidgetsActions.deleteTextWidget.type]: 'delete',
  [PresenterWidgetsActions.createSmartTextWidget.type]: 'create',
  [PresenterWidgetsActions.updateSelectedSmartTextWidget.type]: 'update',
  [PresenterWidgetsActions.deleteSmartTextWidget.type]: 'delete',
  [PresenterWidgetsActions.createTotalRaisedWidget.type]: 'create',
  [PresenterWidgetsActions.updateSelectedTotalRaisedWidget.type]: 'update',
  [PresenterWidgetsActions.deleteTotalRaisedWidget.type]: 'delete',
  [PresenterWidgetsActions.createTeamScoringWidget.type]: 'create',
  [PresenterWidgetsActions.updateSelectedTeamScoringWidget.type]: 'update',
  [PresenterWidgetsActions.deleteTeamScoringWidget.type]: 'delete',
  [PresenterWidgetsActions.createPowerPledgeWidget.type]: 'create',
  [PresenterWidgetsActions.updateSelectedPowerPledgeWidget.type]: 'update',
  [PresenterWidgetsActions.deletePowerPledgeWidget.type]: 'delete',
  [PresenterWidgetsActions.createNotificationWidget.type]: 'create',
  [PresenterWidgetsActions.updateSelectedNotificationWidget.type]: 'update',
  [PresenterWidgetsActions.deleteNotificationWidget.type]: 'delete',
  [PresenterWidgetsActions.createItemTableWidget.type]: 'create',
  [PresenterWidgetsActions.updateSelectedItemTableWidget.type]: 'update',
  [PresenterWidgetsActions.deleteItemTableWidget.type]: 'delete',
  [PresenterWidgetsActions.createRaffleWinnersWidget.type]: 'create',
  [PresenterWidgetsActions.updateSelectedRaffleWinnersWidget.type]: 'update',
  [PresenterWidgetsActions.deleteRaffleWinnersWidget.type]: 'delete',
  [PresenterWidgetsActions.createHonourRollWidget.type]: 'create',
  [PresenterWidgetsActions.updateSelectedHonourRollWidget.type]: 'update',
  [PresenterWidgetsActions.deleteHonourRollWidget.type]: 'delete',
  [PresenterWidgetsActions.createThermometerWidget.type]: 'create',
  [PresenterWidgetsActions.updateSelectedThermometerWidget.type]: 'update',
  [PresenterWidgetsActions.deleteThermometerWidget.type]: 'delete',
  [PresenterWidgetsActions.createRaffleDrawWidget.type]: 'create',
  [PresenterWidgetsActions.updateSelectedRaffleDrawWidget.type]: 'update',
  [PresenterWidgetsActions.deleteRaffleDrawWidget.type]: 'delete',
  [PresenterWidgetsActions.createQrCodeWidget.type]: 'create',
  [PresenterWidgetsActions.updateSelectedQrCodeWidget.type]: 'update',
  [PresenterWidgetsActions.deleteQrCodeWidget.type]: 'delete',
  [PresenterWidgetsActions.createVideoWidget.type]: 'create',
  [PresenterWidgetsActions.updateSelectedVideoWidget.type]: 'update',
  [PresenterWidgetsActions.deleteVideoWidget.type]: 'delete',
}

const WIDGET_TYPE_BY_ACTION_TYPE: Record<string, WidgetType> = {
  [PresenterWidgetsActions.createCountdownWidget.type]: WidgetType.COUNTDOWN,
  [PresenterWidgetsActions.updateSelectedCountdownWidget.type]: WidgetType.COUNTDOWN,
  [PresenterWidgetsActions.deleteCountdownWidget.type]: WidgetType.COUNTDOWN,
  [PresenterWidgetsActions.createImageWidget.type]: WidgetType.IMAGE,
  [PresenterWidgetsActions.updateSelectedImageWidget.type]: WidgetType.IMAGE,
  [PresenterWidgetsActions.deleteImageWidget.type]: WidgetType.IMAGE,
  [PresenterWidgetsActions.createTextWidget.type]: WidgetType.TEXT,
  [PresenterWidgetsActions.updateSelectedTextWidget.type]: WidgetType.TEXT,
  [PresenterWidgetsActions.deleteTextWidget.type]: WidgetType.TEXT,
  [PresenterWidgetsActions.createSmartTextWidget.type]: WidgetType.SMART_TEXT,
  [PresenterWidgetsActions.updateSelectedSmartTextWidget.type]: WidgetType.SMART_TEXT,
  [PresenterWidgetsActions.deleteSmartTextWidget.type]: WidgetType.SMART_TEXT,
  [PresenterWidgetsActions.createTotalRaisedWidget.type]: WidgetType.TOTAL_RAISED,
  [PresenterWidgetsActions.updateSelectedTotalRaisedWidget.type]: WidgetType.TOTAL_RAISED,
  [PresenterWidgetsActions.deleteTotalRaisedWidget.type]: WidgetType.TOTAL_RAISED,
  [PresenterWidgetsActions.createTeamScoringWidget.type]: WidgetType.TEAM_SCORING,
  [PresenterWidgetsActions.updateSelectedTeamScoringWidget.type]: WidgetType.TEAM_SCORING,
  [PresenterWidgetsActions.deleteTeamScoringWidget.type]: WidgetType.TEAM_SCORING,
  [PresenterWidgetsActions.createPowerPledgeWidget.type]: WidgetType.POWER_PLEDGE,
  [PresenterWidgetsActions.updateSelectedPowerPledgeWidget.type]: WidgetType.POWER_PLEDGE,
  [PresenterWidgetsActions.deletePowerPledgeWidget.type]: WidgetType.POWER_PLEDGE,
  [PresenterWidgetsActions.createNotificationWidget.type]: WidgetType.NOTIFICATION,
  [PresenterWidgetsActions.updateSelectedNotificationWidget.type]: WidgetType.NOTIFICATION,
  [PresenterWidgetsActions.deleteNotificationWidget.type]: WidgetType.NOTIFICATION,
  [PresenterWidgetsActions.createItemTableWidget.type]: WidgetType.ITEM_TABLE,
  [PresenterWidgetsActions.updateSelectedItemTableWidget.type]: WidgetType.ITEM_TABLE,
  [PresenterWidgetsActions.deleteItemTableWidget.type]: WidgetType.ITEM_TABLE,
  [PresenterWidgetsActions.createRaffleWinnersWidget.type]: WidgetType.RAFFLE_WINNERS,
  [PresenterWidgetsActions.updateSelectedRaffleWinnersWidget.type]: WidgetType.RAFFLE_WINNERS,
  [PresenterWidgetsActions.deleteRaffleWinnersWidget.type]: WidgetType.RAFFLE_WINNERS,
  [PresenterWidgetsActions.createHonourRollWidget.type]: WidgetType.HONOUR_ROLL,
  [PresenterWidgetsActions.updateSelectedHonourRollWidget.type]: WidgetType.HONOUR_ROLL,
  [PresenterWidgetsActions.deleteHonourRollWidget.type]: WidgetType.HONOUR_ROLL,
  [PresenterWidgetsActions.createThermometerWidget.type]: WidgetType.THERMOMETER,
  [PresenterWidgetsActions.updateSelectedThermometerWidget.type]: WidgetType.THERMOMETER,
  [PresenterWidgetsActions.deleteThermometerWidget.type]: WidgetType.THERMOMETER,
  [PresenterWidgetsActions.createRaffleDrawWidget.type]: WidgetType.RAFFLE_DRAW,
  [PresenterWidgetsActions.updateSelectedRaffleDrawWidget.type]: WidgetType.RAFFLE_DRAW,
  [PresenterWidgetsActions.deleteRaffleDrawWidget.type]: WidgetType.RAFFLE_DRAW,
  [PresenterWidgetsActions.createQrCodeWidget.type]: WidgetType.QR_CODE,
  [PresenterWidgetsActions.updateSelectedQrCodeWidget.type]: WidgetType.QR_CODE,
  [PresenterWidgetsActions.deleteQrCodeWidget.type]: WidgetType.QR_CODE,
  [PresenterWidgetsActions.createVideoWidget.type]: WidgetType.VIDEO,
  [PresenterWidgetsActions.updateSelectedVideoWidget.type]: WidgetType.VIDEO,
  [PresenterWidgetsActions.deleteVideoWidget.type]: WidgetType.VIDEO,
}

@Injectable()
export class PresenterWidgetsEffects {
  private actions$ = inject(Actions)
  private store = inject(Store)
  private presenterWidgetsService = inject(PresenterWidgetsApiService)
  private presenterWidgetsFacade = inject(PresenterWidgetsFacade)
  private selectedAuctionFacade = inject(SelectedAuctionFacade)
  private gbErrorMessageService = inject(GbErrorMessageService)
  private presenterContextService = inject(PresenterContextService)
  private projectorsFacade = inject(ProjectorsFacade)

  private readonly WIDGET_PATH_BY_TYPE: Record<WidgetType, string> = {
    [WidgetType.COUNTDOWN]: 'countdown-widgets',
    [WidgetType.IMAGE]: 'image-widgets',
    [WidgetType.TEXT]: 'text-widgets',
    [WidgetType.SMART_TEXT]: 'smart-text-widgets',
    [WidgetType.TOTAL_RAISED]: 'total-raised-widgets',
    [WidgetType.TEAM_SCORING]: 'team-scoring-widgets',
    [WidgetType.POWER_PLEDGE]: 'power-pledge-widget',
    [WidgetType.NOTIFICATION]: 'notification-widgets',
    [WidgetType.ITEM_TABLE]: 'item-table-widgets',
    [WidgetType.RAFFLE_WINNERS]: 'raffle-winners-widgets',
    [WidgetType.HONOUR_ROLL]: 'honour-roll-widget',
    [WidgetType.THERMOMETER]: 'thermometer-widgets',
    [WidgetType.RAFFLE_DRAW]: 'raffle-draw-widgets',
    [WidgetType.QR_CODE]: 'qr-code-widgets',
    [WidgetType.VIDEO]: 'video-widgets',
  }

  private getWidgetUuidsByType(state: PresenterWidgetsState): Record<WidgetType, Set<string>> {
    return {
      [WidgetType.COUNTDOWN]: new Set((state.countdownWidgets.ids as string[]) ?? []),
      [WidgetType.IMAGE]: new Set((state.imageWidgets.ids as string[]) ?? []),
      [WidgetType.TEXT]: new Set((state.textWidgets.ids as string[]) ?? []),
      [WidgetType.SMART_TEXT]: new Set((state.smartTextWidgets.ids as string[]) ?? []),
      [WidgetType.TOTAL_RAISED]: new Set((state.totalRaisedWidgets.ids as string[]) ?? []),
      [WidgetType.TEAM_SCORING]: new Set((state.teamScoringWidgets.ids as string[]) ?? []),
      [WidgetType.POWER_PLEDGE]: new Set((state.powerPledgeWidgets.ids as string[]) ?? []),
      [WidgetType.NOTIFICATION]: new Set((state.notificationWidgets.ids as string[]) ?? []),
      [WidgetType.ITEM_TABLE]: new Set((state.itemTableWidgets.ids as string[]) ?? []),
      [WidgetType.RAFFLE_WINNERS]: new Set((state.raffleWinnersWidgets.ids as string[]) ?? []),
      [WidgetType.HONOUR_ROLL]: new Set((state.honourRollWidgets.ids as string[]) ?? []),
      [WidgetType.THERMOMETER]: new Set((state.thermometerWidgets.ids as string[]) ?? []),
      [WidgetType.RAFFLE_DRAW]: new Set((state.raffleDrawWidgets.ids as string[]) ?? []),
      [WidgetType.QR_CODE]: new Set((state.qrCodeWidgets.ids as string[]) ?? []),
      [WidgetType.VIDEO]: new Set((state.videoWidgets.ids as string[]) ?? []),
    }
  }

  private readonly WIDGET_CONFIG: WidgetConfigMap = {
    [WidgetType.COUNTDOWN]: {
      entityKey: 'countdownWidget',
      methods: {
        create: 'createCountdownWidget',
        update: 'updateCountdownWidget',
        delete: 'deleteCountdownWidget',
      },
      actions: {
        create: {
          success: PresenterWidgetsActions.createCountdownWidgetSuccess,
          failure: PresenterWidgetsActions.createCountdownWidgetFailure,
        },
        update: {
          success: PresenterWidgetsActions.updateSelectedCountdownWidgetSuccess,
          failure: PresenterWidgetsActions.updateSelectedCountdownWidgetFailure,
        },
        delete: {
          success: PresenterWidgetsActions.deleteCountdownWidgetSuccess,
          failure: PresenterWidgetsActions.deleteCountdownWidgetFailure,
        },
      },
      selectEntities: (state) => PresenterWidgetsSelectors.selectAllCountdownWidgetsList(state),
    },
    [WidgetType.IMAGE]: {
      entityKey: 'imageWidget',
      methods: {
        create: 'createImageWidget',
        update: 'updateImageWidget',
        delete: 'deleteImageWidget',
      },
      actions: {
        create: {
          success: PresenterWidgetsActions.createImageWidgetSuccess,
          failure: PresenterWidgetsActions.createImageWidgetFailure,
        },
        update: {
          success: PresenterWidgetsActions.updateSelectedImageWidgetSuccess,
          failure: PresenterWidgetsActions.updateSelectedImageWidgetFailure,
        },
        delete: {
          success: PresenterWidgetsActions.deleteImageWidgetSuccess,
          failure: PresenterWidgetsActions.deleteImageWidgetFailure,
        },
      },
      selectEntities: (state) => PresenterWidgetsSelectors.selectAllImageWidgetsList(state),
    },
    [WidgetType.TEXT]: {
      entityKey: 'textWidget',
      methods: {
        create: 'createTextWidget',
        update: 'updateTextWidget',
        delete: 'deleteTextWidget',
      },
      actions: {
        create: {
          success: PresenterWidgetsActions.createTextWidgetSuccess,
          failure: PresenterWidgetsActions.createTextWidgetFailure,
        },
        update: {
          success: PresenterWidgetsActions.updateSelectedTextWidgetSuccess,
          failure: PresenterWidgetsActions.updateSelectedTextWidgetFailure,
        },
        delete: {
          success: PresenterWidgetsActions.deleteTextWidgetSuccess,
          failure: PresenterWidgetsActions.deleteTextWidgetFailure,
        },
      },
      selectEntities: (state) => PresenterWidgetsSelectors.selectAllTextWidgetsList(state),
    },
    [WidgetType.SMART_TEXT]: {
      entityKey: 'smartTextWidget',
      methods: {
        create: 'createSmartTextWidget',
        update: 'updateSmartTextWidget',
        delete: 'deleteSmartTextWidget',
      },
      actions: {
        create: {
          success: PresenterWidgetsActions.createSmartTextWidgetSuccess,
          failure: PresenterWidgetsActions.createSmartTextWidgetFailure,
        },
        update: {
          success: PresenterWidgetsActions.updateSelectedSmartTextWidgetSuccess,
          failure: PresenterWidgetsActions.updateSelectedSmartTextWidgetFailure,
        },
        delete: {
          success: PresenterWidgetsActions.deleteSmartTextWidgetSuccess,
          failure: PresenterWidgetsActions.deleteSmartTextWidgetFailure,
        },
      },
      selectEntities: (state) => PresenterWidgetsSelectors.selectAllSmartTextWidgetsList(state),
    },
    [WidgetType.TOTAL_RAISED]: {
      entityKey: 'totalRaisedWidget',
      methods: {
        create: 'createTotalRaisedWidget',
        update: 'updateTotalRaisedWidget',
        delete: 'deleteTotalRaisedWidget',
      },
      actions: {
        create: {
          success: PresenterWidgetsActions.createTotalRaisedWidgetSuccess,
          failure: PresenterWidgetsActions.createTotalRaisedWidgetFailure,
        },
        update: {
          success: PresenterWidgetsActions.updateSelectedTotalRaisedWidgetSuccess,
          failure: PresenterWidgetsActions.updateSelectedTotalRaisedWidgetFailure,
        },
        delete: {
          success: PresenterWidgetsActions.deleteTotalRaisedWidgetSuccess,
          failure: PresenterWidgetsActions.deleteTotalRaisedWidgetFailure,
        },
      },
      selectEntities: (state) => PresenterWidgetsSelectors.selectAllTotalRaisedWidgetsList(state),
    },
    [WidgetType.TEAM_SCORING]: {
      entityKey: 'teamScoringWidget',
      methods: {
        create: 'createTeamScoringWidget',
        update: 'updateTeamScoringWidget',
        delete: 'deleteTeamScoringWidget',
      },
      actions: {
        create: {
          success: PresenterWidgetsActions.createTeamScoringWidgetSuccess,
          failure: PresenterWidgetsActions.createTeamScoringWidgetFailure,
        },
        update: {
          success: PresenterWidgetsActions.updateSelectedTeamScoringWidgetSuccess,
          failure: PresenterWidgetsActions.updateSelectedTeamScoringWidgetFailure,
        },
        delete: {
          success: PresenterWidgetsActions.deleteTeamScoringWidgetSuccess,
          failure: PresenterWidgetsActions.deleteTeamScoringWidgetFailure,
        },
      },
      selectEntities: (state) => PresenterWidgetsSelectors.selectAllTeamScoringWidgetsList(state),
    },
    [WidgetType.POWER_PLEDGE]: {
      entityKey: 'powerPledgeWidget',
      methods: {
        create: 'createPowerPledgeWidget',
        update: 'updatePowerPledgeWidget',
        delete: 'deletePowerPledgeWidget',
      },
      actions: {
        create: {
          success: PresenterWidgetsActions.createPowerPledgeWidgetSuccess,
          failure: PresenterWidgetsActions.createPowerPledgeWidgetFailure,
        },
        update: {
          success: PresenterWidgetsActions.updateSelectedPowerPledgeWidgetSuccess,
          failure: PresenterWidgetsActions.updateSelectedPowerPledgeWidgetFailure,
        },
        delete: {
          success: PresenterWidgetsActions.deletePowerPledgeWidgetSuccess,
          failure: PresenterWidgetsActions.deletePowerPledgeWidgetFailure,
        },
      },
      selectEntities: (state) => PresenterWidgetsSelectors.selectAllPowerPledgeWidgetsList(state),
    },
    [WidgetType.NOTIFICATION]: {
      entityKey: 'notificationWidget',
      methods: {
        create: 'createNotificationWidget',
        update: 'updateNotificationWidget',
        delete: 'deleteNotificationWidget',
      },
      actions: {
        create: {
          success: PresenterWidgetsActions.createNotificationWidgetSuccess,
          failure: PresenterWidgetsActions.createNotificationWidgetFailure,
        },
        update: {
          success: PresenterWidgetsActions.updateSelectedNotificationWidgetSuccess,
          failure: PresenterWidgetsActions.updateSelectedNotificationWidgetFailure,
        },
        delete: {
          success: PresenterWidgetsActions.deleteNotificationWidgetSuccess,
          failure: PresenterWidgetsActions.deleteNotificationWidgetFailure,
        },
      },
      selectEntities: (state) => PresenterWidgetsSelectors.selectAllNotificationWidgetsList(state),
    },
    [WidgetType.ITEM_TABLE]: {
      entityKey: 'itemTableWidget',
      methods: {
        create: 'createItemTableWidget',
        update: 'updateItemTableWidget',
        delete: 'deleteItemTableWidget',
      },
      actions: {
        create: {
          success: PresenterWidgetsActions.createItemTableWidgetSuccess,
          failure: PresenterWidgetsActions.createItemTableWidgetFailure,
        },
        update: {
          success: PresenterWidgetsActions.updateSelectedItemTableWidgetSuccess,
          failure: PresenterWidgetsActions.updateSelectedItemTableWidgetFailure,
        },
        delete: {
          success: PresenterWidgetsActions.deleteItemTableWidgetSuccess,
          failure: PresenterWidgetsActions.deleteItemTableWidgetFailure,
        },
      },
      selectEntities: (state) => PresenterWidgetsSelectors.selectAllItemTableWidgetsList(state),
    },
    [WidgetType.RAFFLE_WINNERS]: {
      entityKey: 'raffleWinnersWidget',
      methods: {
        create: 'createRaffleWinnersWidget',
        update: 'updateRaffleWinnersWidget',
        delete: 'deleteRaffleWinnersWidget',
      },
      actions: {
        create: {
          success: PresenterWidgetsActions.createRaffleWinnersWidgetSuccess,
          failure: PresenterWidgetsActions.createRaffleWinnersWidgetFailure,
        },
        update: {
          success: PresenterWidgetsActions.updateSelectedRaffleWinnersWidgetSuccess,
          failure: PresenterWidgetsActions.updateSelectedRaffleWinnersWidgetFailure,
        },
        delete: {
          success: PresenterWidgetsActions.deleteRaffleWinnersWidgetSuccess,
          failure: PresenterWidgetsActions.deleteRaffleWinnersWidgetFailure,
        },
      },
      selectEntities: (state) => PresenterWidgetsSelectors.selectAllRaffleWinnersWidgetsList(state),
    },
    [WidgetType.HONOUR_ROLL]: {
      entityKey: 'honourRollWidget',
      methods: {
        create: 'createHonourRollWidget',
        update: 'updateHonourRollWidget',
        delete: 'deleteHonourRollWidget',
      },
      actions: {
        create: {
          success: PresenterWidgetsActions.createHonourRollWidgetSuccess,
          failure: PresenterWidgetsActions.createHonourRollWidgetFailure,
        },
        update: {
          success: PresenterWidgetsActions.updateSelectedHonourRollWidgetSuccess,
          failure: PresenterWidgetsActions.updateSelectedHonourRollWidgetFailure,
        },
        delete: {
          success: PresenterWidgetsActions.deleteHonourRollWidgetSuccess,
          failure: PresenterWidgetsActions.deleteHonourRollWidgetFailure,
        },
      },
      selectEntities: (state) => PresenterWidgetsSelectors.selectAllHonourRollWidgetsList(state),
    },
    [WidgetType.THERMOMETER]: {
      entityKey: 'thermometerWidget',
      methods: {
        create: 'createThermometerWidget',
        update: 'updateThermometerWidget',
        delete: 'deleteThermometerWidget',
      },
      actions: {
        create: {
          success: PresenterWidgetsActions.createThermometerWidgetSuccess,
          failure: PresenterWidgetsActions.createThermometerWidgetFailure,
        },
        update: {
          success: PresenterWidgetsActions.updateSelectedThermometerWidgetSuccess,
          failure: PresenterWidgetsActions.updateSelectedThermometerWidgetFailure,
        },
        delete: {
          success: PresenterWidgetsActions.deleteThermometerWidgetSuccess,
          failure: PresenterWidgetsActions.deleteThermometerWidgetFailure,
        },
      },
      selectEntities: (state) => PresenterWidgetsSelectors.selectAllThermometerWidgetsList(state),
    },
    [WidgetType.RAFFLE_DRAW]: {
      entityKey: 'raffleDrawWidget',
      methods: {
        create: 'createRaffleDrawWidget',
        update: 'updateRaffleDrawWidget',
        delete: 'deleteRaffleDrawWidget',
      },
      actions: {
        create: {
          success: PresenterWidgetsActions.createRaffleDrawWidgetSuccess,
          failure: PresenterWidgetsActions.createRaffleDrawWidgetFailure,
        },
        update: {
          success: PresenterWidgetsActions.updateSelectedRaffleDrawWidgetSuccess,
          failure: PresenterWidgetsActions.updateSelectedRaffleDrawWidgetFailure,
        },
        delete: {
          success: PresenterWidgetsActions.deleteRaffleDrawWidgetSuccess,
          failure: PresenterWidgetsActions.deleteRaffleDrawWidgetFailure,
        },
      },
      selectEntities: (state) => PresenterWidgetsSelectors.selectAllRaffleDrawWidgetsList(state),
    },
    [WidgetType.QR_CODE]: {
      entityKey: 'qrCodeWidget',
      methods: {
        create: 'createQrCodeWidget',
        update: 'updateQrCodeWidget',
        delete: 'deleteQrCodeWidget',
      },
      actions: {
        create: {
          success: PresenterWidgetsActions.createQrCodeWidgetSuccess,
          failure: PresenterWidgetsActions.createQrCodeWidgetFailure,
        },
        update: {
          success: PresenterWidgetsActions.updateSelectedQrCodeWidgetSuccess,
          failure: PresenterWidgetsActions.updateSelectedQrCodeWidgetFailure,
        },
        delete: {
          success: PresenterWidgetsActions.deleteQrCodeWidgetSuccess,
          failure: PresenterWidgetsActions.deleteQrCodeWidgetFailure,
        },
      },
      selectEntities: (state) => PresenterWidgetsSelectors.selectAllQrCodeWidgetsList(state),
    },
    [WidgetType.VIDEO]: {
      entityKey: 'videoWidget',
      methods: {
        create: 'createVideoWidget',
        update: 'updateVideoWidget',
        delete: 'deleteVideoWidget',
      },
      actions: {
        create: {
          success: PresenterWidgetsActions.createVideoWidgetSuccess,
          failure: PresenterWidgetsActions.createVideoWidgetFailure,
        },
        update: {
          success: PresenterWidgetsActions.updateSelectedVideoWidgetSuccess,
          failure: PresenterWidgetsActions.updateSelectedVideoWidgetFailure,
        },
        delete: {
          success: PresenterWidgetsActions.deleteVideoWidgetSuccess,
          failure: PresenterWidgetsActions.deleteVideoWidgetFailure,
        },
      },
      selectEntities: (state) => PresenterWidgetsSelectors.selectAllVideoWidgetsList(state),
    },
  }

  private callServiceCreate<T extends WidgetModel>(
    method: string,
    urlName: string,
    payload: Omit<T, 'uuid'>,
  ): Observable<T> {
    const fn = this.presenterWidgetsService[method as keyof PresenterWidgetsApiService] as unknown as (
      urlName: string,
      payload: Omit<T, 'uuid'>,
    ) => Observable<T>

    return fn.call(this.presenterWidgetsService, urlName, payload)
  }

  private callServiceUpdate<T extends WidgetModel>(
    method: string,
    urlName: string,
    payload: Partial<T> & Pick<T, 'uuid'>,
  ): Observable<T> {
    const fn = this.presenterWidgetsService[method as keyof PresenterWidgetsApiService] as unknown as (
      urlName: string,
      payload: Partial<T> & Pick<T, 'uuid'>,
    ) => Observable<T>

    return fn.call(this.presenterWidgetsService, urlName, payload)
  }

  private handleWidgetAction<K extends WidgetType>(
    widgetType: K,
    operation: WidgetOperation,
    config: WidgetConfigMap[K],
    urlName: string,
    action: { type: string } & Record<string, unknown>,
    selectedWidget: WidgetModel | null,
    selectedProjectorId: string | undefined,
    nextZIndex: number,
  ): Observable<Action> {
    const autoPopulateQrCodeUrl = <T extends QrCodeWidget>(payload: T, urlName: string): T => {
      if (config.entityKey === 'qrCodeWidget' && !payload.url) {
        const appUrl = `${this.appConfig.frontendUrl}${urlName}`
        return { ...payload, url: appUrl }
      }
      return payload
    }

    if (operation === 'create') {
      let payload = action['widget'] as Omit<WidgetModelMap[K], 'uuid'>

      if (this.presenterContextService.isTemplateMode) {
        const uuid = crypto.randomUUID()
        payload = autoPopulateQrCodeUrl(payload as QrCodeWidget, '') as typeof payload
        const newWidget = { ...payload, uuid, projector: 'template', z: nextZIndex } as WidgetModelMap[K]
        return of(config.actions.create.success({ widget: newWidget }))
      }

      payload = autoPopulateQrCodeUrl(payload as QrCodeWidget, urlName) as typeof payload

      const projector = (payload as Partial<WidgetModel>).projector ?? selectedProjectorId

      const payloadWithProjector = { ...payload, projector, z: nextZIndex } as Omit<WidgetModelMap[K], 'uuid'>

      return this.callServiceCreate<WidgetModelMap[K]>(config.methods.create, urlName, payloadWithProjector).pipe(
        map((response) => config.actions.create.success({ widget: response })),
        catchError((error: HttpErrorResponse) => {
          this.gbErrorMessageService.showError(error)
          return of(config.actions.create.failure({ error }))
        }),
      )
    }

    if (operation === 'update') {
      let changes = action['widget'] as Partial<WidgetModelMap[K]>
      const uuid = (changes as Partial<WidgetModel>).uuid ?? selectedWidget?.uuid

      if (!uuid) {
        return of(config.actions.update.failure({ error: new HttpErrorResponse({ status: 400 }) }))
      }

      if (this.presenterContextService.isTemplateMode) {
        const update: Update<WidgetModelMap[K]> = { id: uuid, changes }
        return of(config.actions.update.success({ update }))
      }

      changes = autoPopulateQrCodeUrl(changes as QrCodeWidget, urlName) as typeof changes

      const payload = { ...changes, uuid } as Partial<WidgetModelMap[K]> & Pick<WidgetModelMap[K], 'uuid'>

      return this.callServiceUpdate<WidgetModelMap[K]>(config.methods.update, urlName, payload).pipe(
        map((response) => config.actions.update.success({ update: { id: response.uuid, changes: response } })),
        catchError((error: HttpErrorResponse) => {
          this.gbErrorMessageService.showError(error)
          return of(config.actions.update.failure({ error }))
        }),
      )
    }

    const uuid = action['uuid'] as string

    if (this.presenterContextService.isTemplateMode) {
      return of(config.actions.delete.success({ uuid }))
    }

    const widgetPath = this.WIDGET_PATH_BY_TYPE[widgetType]
    return this.presenterWidgetsService.softDeleteWidget(urlName, widgetPath, uuid).pipe(
      map(() => config.actions.delete.success({ uuid })),
      catchError((error: HttpErrorResponse) => {
        this.gbErrorMessageService.showError(error)
        return of(config.actions.delete.failure({ error }))
      }),
    )
  }

  handleWidgetOperations$ = createEffect(
    (): Observable<Action> =>
      this.actions$.pipe(
        ofType(...ALL_WIDGET_START_ACTIONS),
        withLatestFrom(
          this.selectedAuctionFacade.urlName$,
          this.presenterWidgetsFacade.selectedWidget$,
          this.projectorsFacade.selectedProjectorId$,
          this.presenterWidgetsFacade.allWidgets$,
        ),
        mergeMap(([action, urlName, selectedWidget, selectedProjectorId, allWidgets]) => {
          const widgetType = WIDGET_TYPE_BY_ACTION_TYPE[action.type]
          const operation = OPERATION_BY_ACTION_TYPE[action.type]
          const config = this.WIDGET_CONFIG[widgetType]

          const maxZIndex = allWidgets.reduce((max, w) => Math.max(max, w.z ?? 0), 0)
          const nextZIndex = maxZIndex + 1

          return this.handleWidgetAction(
            widgetType,
            operation,
            config,
            urlName,
            action as never,
            selectedWidget,
            selectedProjectorId,
            nextZIndex,
          )
        }),
      ),
  )

  private readonly createSuccessActions = Object.values(this.WIDGET_CONFIG).map(
    (config) => config.actions.create.success,
  )

  private readonly widgetTypeBySuccessAction = Object.entries(this.WIDGET_CONFIG).reduce(
    (acc, [type, config]) => {
      acc[config.actions.create.success.type] = type as WidgetType
      return acc
    },
    {} as Record<string, WidgetType>,
  )

  autoSelectCreatedWidget$ = createEffect(() =>
    this.actions$.pipe(
      ofType(...this.createSuccessActions),
      map((action) =>
        PresenterWidgetsActions.selectWidget({
          widget: action.widget,
          widgetType: this.widgetTypeBySuccessAction[action.type],
        }),
      ),
    ),
  )

  persistGenericSelectedWidgetUpdates$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PresenterWidgetsActions.updateSelectedWidget),
      filter(() => !this.presenterContextService.isTemplateMode),
      withLatestFrom(this.presenterWidgetsFacade.selectedWidgetType$),
      map(([action, widgetType]) => {
        if (!widgetType) {
          return PresenterWidgetsActions.updateSelectedTextWidgetFailure({
            error: new HttpErrorResponse({ status: 400 }),
          })
        }

        switch (widgetType) {
          case WidgetType.COUNTDOWN:
            return PresenterWidgetsActions.updateSelectedCountdownWidget({ widget: action.widget as never })
          case WidgetType.IMAGE:
            return PresenterWidgetsActions.updateSelectedImageWidget({ widget: action.widget as never })
          case WidgetType.TEXT:
            return PresenterWidgetsActions.updateSelectedTextWidget({ widget: action.widget as never })
          case WidgetType.SMART_TEXT:
            return PresenterWidgetsActions.updateSelectedSmartTextWidget({ widget: action.widget as never })
          case WidgetType.TOTAL_RAISED:
            return PresenterWidgetsActions.updateSelectedTotalRaisedWidget({ widget: action.widget as never })
          case WidgetType.TEAM_SCORING:
            return PresenterWidgetsActions.updateSelectedTeamScoringWidget({ widget: action.widget as never })
          case WidgetType.POWER_PLEDGE:
            return PresenterWidgetsActions.updateSelectedPowerPledgeWidget({ widget: action.widget as never })
          case WidgetType.NOTIFICATION:
            return PresenterWidgetsActions.updateSelectedNotificationWidget({ widget: action.widget as never })
          case WidgetType.ITEM_TABLE:
            return PresenterWidgetsActions.updateSelectedItemTableWidget({ widget: action.widget as never })
          case WidgetType.RAFFLE_WINNERS:
            return PresenterWidgetsActions.updateSelectedRaffleWinnersWidget({ widget: action.widget as never })
          case WidgetType.HONOUR_ROLL:
            return PresenterWidgetsActions.updateSelectedHonourRollWidget({ widget: action.widget as never })
          case WidgetType.THERMOMETER:
            return PresenterWidgetsActions.updateSelectedThermometerWidget({ widget: action.widget as never })
          case WidgetType.RAFFLE_DRAW:
            return PresenterWidgetsActions.updateSelectedRaffleDrawWidget({ widget: action.widget as never })
          case WidgetType.QR_CODE:
            return PresenterWidgetsActions.updateSelectedQrCodeWidget({ widget: action.widget as never })
          case WidgetType.VIDEO:
            return PresenterWidgetsActions.updateSelectedVideoWidget({ widget: action.widget as never })
          default:
            return PresenterWidgetsActions.updateSelectedTextWidgetFailure({
              error: new HttpErrorResponse({ status: 400 }),
            })
        }
      }),
    ),
  )

  deleteSelectedWidget$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PresenterWidgetsActions.deleteSelectedWidget),
      withLatestFrom(this.presenterWidgetsFacade.selectedWidget$, this.presenterWidgetsFacade.selectedWidgetType$),
      filter(([, selectedWidget, selectedWidgetType]) => !!selectedWidget && !!selectedWidgetType),
      map(([, selectedWidget, selectedWidgetType]) => {
        const uuid = (selectedWidget as WidgetModel).uuid

        switch (selectedWidgetType) {
          case WidgetType.COUNTDOWN:
            return PresenterWidgetsActions.deleteCountdownWidget({ uuid })
          case WidgetType.IMAGE:
            return PresenterWidgetsActions.deleteImageWidget({ uuid })
          case WidgetType.TEXT:
            return PresenterWidgetsActions.deleteTextWidget({ uuid })
          case WidgetType.SMART_TEXT:
            return PresenterWidgetsActions.deleteSmartTextWidget({ uuid })
          case WidgetType.TOTAL_RAISED:
            return PresenterWidgetsActions.deleteTotalRaisedWidget({ uuid })
          case WidgetType.TEAM_SCORING:
            return PresenterWidgetsActions.deleteTeamScoringWidget({ uuid })
          case WidgetType.POWER_PLEDGE:
            return PresenterWidgetsActions.deletePowerPledgeWidget({ uuid })
          case WidgetType.NOTIFICATION:
            return PresenterWidgetsActions.deleteNotificationWidget({ uuid })
          case WidgetType.ITEM_TABLE:
            return PresenterWidgetsActions.deleteItemTableWidget({ uuid })
          case WidgetType.RAFFLE_WINNERS:
            return PresenterWidgetsActions.deleteRaffleWinnersWidget({ uuid })
          case WidgetType.HONOUR_ROLL:
            return PresenterWidgetsActions.deleteHonourRollWidget({ uuid })
          case WidgetType.THERMOMETER:
            return PresenterWidgetsActions.deleteThermometerWidget({ uuid })
          case WidgetType.RAFFLE_DRAW:
            return PresenterWidgetsActions.deleteRaffleDrawWidget({ uuid })
          case WidgetType.QR_CODE:
            return PresenterWidgetsActions.deleteQrCodeWidget({ uuid })
          case WidgetType.VIDEO:
            return PresenterWidgetsActions.deleteVideoWidget({ uuid })
          default:
            return PresenterWidgetsActions.clearSelectedWidget()
        }
      }),
    ),
  )

  clearSelectedWidgetAfterDeleteSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        PresenterWidgetsActions.deleteCountdownWidgetSuccess,
        PresenterWidgetsActions.deleteImageWidgetSuccess,
        PresenterWidgetsActions.deleteTextWidgetSuccess,
        PresenterWidgetsActions.deleteSmartTextWidgetSuccess,
        PresenterWidgetsActions.deleteTotalRaisedWidgetSuccess,
        PresenterWidgetsActions.deleteTeamScoringWidgetSuccess,
        PresenterWidgetsActions.deletePowerPledgeWidgetSuccess,
        PresenterWidgetsActions.deleteNotificationWidgetSuccess,
        PresenterWidgetsActions.deleteItemTableWidgetSuccess,
        PresenterWidgetsActions.deleteRaffleWinnersWidgetSuccess,
        PresenterWidgetsActions.deleteHonourRollWidgetSuccess,
        PresenterWidgetsActions.deleteThermometerWidgetSuccess,
        PresenterWidgetsActions.deleteRaffleDrawWidgetSuccess,
        PresenterWidgetsActions.deleteQrCodeWidgetSuccess,
        PresenterWidgetsActions.deleteVideoWidgetSuccess,
      ),
      map(() => PresenterWidgetsActions.clearSelectedWidget()),
    ),
  )

  clearSelectedWidgetAfterDeleteStart$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        PresenterWidgetsActions.deleteCountdownWidget,
        PresenterWidgetsActions.deleteImageWidget,
        PresenterWidgetsActions.deleteTextWidget,
        PresenterWidgetsActions.deleteSmartTextWidget,
        PresenterWidgetsActions.deleteTotalRaisedWidget,
        PresenterWidgetsActions.deleteTeamScoringWidget,
        PresenterWidgetsActions.deletePowerPledgeWidget,
        PresenterWidgetsActions.deleteNotificationWidget,
        PresenterWidgetsActions.deleteItemTableWidget,
        PresenterWidgetsActions.deleteRaffleWinnersWidget,
        PresenterWidgetsActions.deleteHonourRollWidget,
        PresenterWidgetsActions.deleteThermometerWidget,
        PresenterWidgetsActions.deleteRaffleDrawWidget,
        PresenterWidgetsActions.deleteQrCodeWidget,
        PresenterWidgetsActions.deleteVideoWidget,
      ),
      map(() => PresenterWidgetsActions.clearSelectedWidget()),
    ),
  )

  loadCountdownWidgets$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PresenterWidgetsActions.loadCountdownWidgets),
      withLatestFrom(this.selectedAuctionFacade.urlName$),
      switchMap(([, urlName]) =>
        this.presenterWidgetsService.getCountdownWidgets(urlName).pipe(
          map((widgets) => PresenterWidgetsActions.loadCountdownWidgetsSuccess({ widgets })),
          catchError((error: HttpErrorResponse) => of(PresenterWidgetsActions.loadCountdownWidgetsFailure({ error }))),
        ),
      ),
    ),
  )

  loadImageWidgets$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PresenterWidgetsActions.loadImageWidgets),
      withLatestFrom(this.selectedAuctionFacade.urlName$),
      switchMap(([, urlName]) =>
        this.presenterWidgetsService.getImageWidgets(urlName).pipe(
          map((widgets) => PresenterWidgetsActions.loadImageWidgetsSuccess({ widgets })),
          catchError((error: HttpErrorResponse) => of(PresenterWidgetsActions.loadImageWidgetsFailure({ error }))),
        ),
      ),
    ),
  )

  loadTextWidgets$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PresenterWidgetsActions.loadTextWidgets),
      withLatestFrom(this.selectedAuctionFacade.urlName$),
      switchMap(([, urlName]) =>
        this.presenterWidgetsService.getTextWidgets(urlName).pipe(
          map((widgets) => PresenterWidgetsActions.loadTextWidgetsSuccess({ widgets })),
          catchError((error: HttpErrorResponse) => of(PresenterWidgetsActions.loadTextWidgetsFailure({ error }))),
        ),
      ),
    ),
  )

  loadSmartTextWidgets$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PresenterWidgetsActions.loadSmartTextWidgets),
      withLatestFrom(this.selectedAuctionFacade.urlName$),
      switchMap(([, urlName]) =>
        this.presenterWidgetsService.getSmartTextWidgets(urlName).pipe(
          map((widgets) => PresenterWidgetsActions.loadSmartTextWidgetsSuccess({ widgets })),
          catchError((error: HttpErrorResponse) => of(PresenterWidgetsActions.loadSmartTextWidgetsFailure({ error }))),
        ),
      ),
    ),
  )

  loadTotalRaisedWidgets$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PresenterWidgetsActions.loadTotalRaisedWidgets),
      withLatestFrom(this.selectedAuctionFacade.urlName$),
      switchMap(([, urlName]) =>
        this.presenterWidgetsService.getTotalRaisedWidgets(urlName).pipe(
          map((widgets) => PresenterWidgetsActions.loadTotalRaisedWidgetsSuccess({ widgets })),
          catchError((error: HttpErrorResponse) =>
            of(PresenterWidgetsActions.loadTotalRaisedWidgetsFailure({ error })),
          ),
        ),
      ),
    ),
  )

  loadTeamScoringWidgets$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PresenterWidgetsActions.loadTeamScoringWidgets),
      withLatestFrom(this.selectedAuctionFacade.urlName$),
      switchMap(([, urlName]) =>
        this.presenterWidgetsService.getTeamScoringWidgets(urlName).pipe(
          map((widgets) => PresenterWidgetsActions.loadTeamScoringWidgetsSuccess({ widgets })),
          catchError((error: HttpErrorResponse) =>
            of(PresenterWidgetsActions.loadTeamScoringWidgetsFailure({ error })),
          ),
        ),
      ),
    ),
  )

  loadPowerPledgeWidgets$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PresenterWidgetsActions.loadPowerPledgeWidgets),
      withLatestFrom(this.selectedAuctionFacade.urlName$),
      switchMap(([, urlName]) =>
        this.presenterWidgetsService.getPowerPledgeWidgets(urlName).pipe(
          map((widgets) => PresenterWidgetsActions.loadPowerPledgeWidgetsSuccess({ widgets })),
          catchError((error: HttpErrorResponse) =>
            of(PresenterWidgetsActions.loadPowerPledgeWidgetsFailure({ error })),
          ),
        ),
      ),
    ),
  )

  loadNotificationWidgets$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PresenterWidgetsActions.loadNotificationWidgets),
      withLatestFrom(this.selectedAuctionFacade.urlName$),
      switchMap(([, urlName]) =>
        this.presenterWidgetsService.getNotificationWidgets(urlName).pipe(
          map((widgets) => PresenterWidgetsActions.loadNotificationWidgetsSuccess({ widgets })),
          catchError((error: HttpErrorResponse) =>
            of(PresenterWidgetsActions.loadNotificationWidgetsFailure({ error })),
          ),
        ),
      ),
    ),
  )

  loadItemTableWidgets$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PresenterWidgetsActions.loadItemTableWidgets),
      withLatestFrom(this.selectedAuctionFacade.urlName$),
      switchMap(([, urlName]) =>
        this.presenterWidgetsService.getItemTableWidgets(urlName).pipe(
          map((widgets) => PresenterWidgetsActions.loadItemTableWidgetsSuccess({ widgets })),
          catchError((error: HttpErrorResponse) => of(PresenterWidgetsActions.loadItemTableWidgetsFailure({ error }))),
        ),
      ),
    ),
  )

  loadRaffleWinnersWidgets$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PresenterWidgetsActions.loadRaffleWinnersWidgets),
      withLatestFrom(this.selectedAuctionFacade.urlName$),
      switchMap(([, urlName]) =>
        this.presenterWidgetsService.getRaffleWinnersWidgets(urlName).pipe(
          map((widgets) => PresenterWidgetsActions.loadRaffleWinnersWidgetsSuccess({ widgets })),
          catchError((error: HttpErrorResponse) =>
            of(PresenterWidgetsActions.loadRaffleWinnersWidgetsFailure({ error })),
          ),
        ),
      ),
    ),
  )

  loadHonourRollWidgets$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PresenterWidgetsActions.loadHonourRollWidgets),
      withLatestFrom(this.selectedAuctionFacade.urlName$),
      switchMap(([, urlName]) =>
        this.presenterWidgetsService.getHonourRollWidgets(urlName).pipe(
          map((widgets) => PresenterWidgetsActions.loadHonourRollWidgetsSuccess({ widgets })),
          catchError((error: HttpErrorResponse) => of(PresenterWidgetsActions.loadHonourRollWidgetsFailure({ error }))),
        ),
      ),
    ),
  )

  loadThermometerWidgets$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PresenterWidgetsActions.loadThermometerWidgets),
      withLatestFrom(this.selectedAuctionFacade.urlName$),
      switchMap(([, urlName]) =>
        this.presenterWidgetsService.getThermometerWidgets(urlName).pipe(
          map((widgets) => PresenterWidgetsActions.loadThermometerWidgetsSuccess({ widgets })),
          catchError((error: HttpErrorResponse) =>
            of(PresenterWidgetsActions.loadThermometerWidgetsFailure({ error })),
          ),
        ),
      ),
    ),
  )

  loadRaffleDrawWidgets$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PresenterWidgetsActions.loadRaffleDrawWidgets),
      withLatestFrom(this.selectedAuctionFacade.urlName$),
      switchMap(([, urlName]) =>
        this.presenterWidgetsService.getRaffleDrawWidgets(urlName).pipe(
          map((widgets) => PresenterWidgetsActions.loadRaffleDrawWidgetsSuccess({ widgets })),
          catchError((error: HttpErrorResponse) => of(PresenterWidgetsActions.loadRaffleDrawWidgetsFailure({ error }))),
        ),
      ),
    ),
  )

  loadQrCodeWidgets$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PresenterWidgetsActions.loadQrCodeWidgets),
      withLatestFrom(this.selectedAuctionFacade.urlName$),
      switchMap(([, urlName]) =>
        this.presenterWidgetsService.getQrCodeWidgets(urlName).pipe(
          map((widgets) => PresenterWidgetsActions.loadQrCodeWidgetsSuccess({ widgets })),
          catchError((error: HttpErrorResponse) => of(PresenterWidgetsActions.loadQrCodeWidgetsFailure({ error }))),
        ),
      ),
    ),
  )

  loadVideoWidgets$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PresenterWidgetsActions.loadVideoWidgets),
      withLatestFrom(this.selectedAuctionFacade.urlName$),
      switchMap(([, urlName]) =>
        this.presenterWidgetsService.getVideoWidgets(urlName).pipe(
          map((widgets) => PresenterWidgetsActions.loadVideoWidgetsSuccess({ widgets })),
          catchError((error: HttpErrorResponse) => of(PresenterWidgetsActions.loadVideoWidgetsFailure({ error }))),
        ),
      ),
    ),
  )

  constructor(@Inject(APP_CONFIG) private appConfig: EnvironmentModel) {}

  syncUndoRedoWithBackend$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(PresenterWidgetsActions.undo, PresenterWidgetsActions.redo),
        filter(() => !this.presenterContextService.isTemplateMode),
        withLatestFrom(
          this.selectedAuctionFacade.urlName$,
          this.store.select(PresenterWidgetsSelectors.selectPresenterWidgetsPresentState),
          this.store.select(PresenterWidgetsSelectors.selectPresenterWidgetsPastStates),
          this.store.select(PresenterWidgetsSelectors.selectPresenterWidgetsFutureStates),
        ),
        switchMap(([action, urlName, present, past, future]) => {
          const previousPresent = action.type === PresenterWidgetsActions.undo.type ? future[0] : past[past.length - 1]
          if (!previousPresent) {
            return of(null)
          }

          const previousByType = this.getWidgetUuidsByType(previousPresent)
          const presentByType = this.getWidgetUuidsByType(present)

          const operations: Array<{ widgetType: WidgetType; uuid: string; op: 'restore' | 'soft-delete' }> = []

          ;(Object.values(WidgetType) as WidgetType[]).forEach((widgetType) => {
            const previousUuids = previousByType[widgetType]
            const presentUuids = presentByType[widgetType]

            if (!previousUuids || !presentUuids) {
              return
            }

            for (const uuid of presentUuids) {
              if (!previousUuids.has(uuid)) {
                operations.push({ widgetType, uuid, op: 'restore' })
              }
            }

            for (const uuid of previousUuids) {
              if (!presentUuids.has(uuid)) {
                operations.push({ widgetType, uuid, op: 'soft-delete' })
              }
            }
          })

          if (!operations.length) {
            return of(null)
          }

          return from(operations).pipe(
            mergeMap(({ widgetType, uuid, op }) => {
              const widgetPath = this.WIDGET_PATH_BY_TYPE[widgetType]
              if (!widgetPath) {
                return of(null)
              }

              const request$ =
                op === 'restore'
                  ? this.presenterWidgetsService.restoreWidget<unknown>(urlName, widgetPath, uuid)
                  : this.presenterWidgetsService.softDeleteWidget(urlName, widgetPath, uuid)

              return request$.pipe(
                catchError((error: HttpErrorResponse) => {
                  this.gbErrorMessageService.showError(error)
                  return of(null)
                }),
              )
            }, 2),
          )
        }),
      ),
    { dispatch: false },
  )
}
